import { Award, Eye, Handshake, Building2 } from "lucide-react";
import { useSiteSettings } from "@/lib/cms";
import { useDocumentTitle } from "@/lib/use-document-title";
import { ScrollReveal } from "@/components/scroll-reveal";

export function AboutPage() {
  const { about, brand } = useSiteSettings();
  useDocumentTitle(about.pageTitle.split("\n")[0]);

  return (
    <div>
      {/* Hero band with watermark */}
      <section
        data-preview-section="about-hero"
        className="relative bg-white py-24 lg:py-32 section-titled overflow-hidden"
      >
        <div className="section-watermark uppercase">rólunk</div>
        <div className="container relative z-10 max-w-4xl text-center">
          <ScrollReveal immediate>
            <span className="section-eyebrow">{about.pageEyebrow}</span>
            <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-heading font-normal uppercase tracking-ultrawide leading-tight whitespace-pre-line">
              {about.pageTitle.split(" ").slice(0, -2).join(" ")}{" "}
              <span className="text-accent">
                {about.pageTitle.split(" ").slice(-2).join(" ")}
              </span>
            </h1>
            <p className="mt-8 text-base lg:text-lg text-brand-500 max-w-2xl mx-auto leading-relaxed">
              {about.intro}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Vision + values */}
      <section className="py-20 lg:py-28 bg-brand-50">
        <div className="container max-w-5xl grid md:grid-cols-2 gap-14">
          <ScrollReveal>
            <div className="w-14 h-14 bg-white text-accent grid place-items-center mb-6 border border-brand-200">
              <Eye size={26} strokeWidth={1.5} />
            </div>
            <span className="section-eyebrow">01</span>
            <h2 className="text-xl font-semibold uppercase tracking-widish text-brand-700 mt-2">
              {about.visionTitle}
            </h2>
            <p className="mt-4 text-brand-500 leading-relaxed whitespace-pre-line">
              {about.visionText}
            </p>
          </ScrollReveal>

          <ScrollReveal delay={1}>
            <div className="w-14 h-14 bg-white text-accent grid place-items-center mb-6 border border-brand-200">
              <Award size={26} strokeWidth={1.5} />
            </div>
            <span className="section-eyebrow">02</span>
            <h2 className="text-xl font-semibold uppercase tracking-widish text-brand-700 mt-2">
              {about.valuesTitle}
            </h2>
            <ul className="mt-4 space-y-3">
              {about.values.map((value, i) => (
                <li
                  key={`${i}-${value}`}
                  className="flex items-start gap-3 text-brand-500 leading-relaxed"
                >
                  <Handshake
                    size={18}
                    className="text-accent mt-0.5 shrink-0"
                  />
                  <span>{value}</span>
                </li>
              ))}
            </ul>
          </ScrollReveal>
        </div>
      </section>

      {/* Stats strip */}
      {about.stats.length > 0 && (
        <section className="bg-brand-900 text-white py-20">
          <div
            className="container grid gap-8 text-center max-w-4xl"
            style={{
              gridTemplateColumns: `repeat(${Math.min(about.stats.length, 4)}, minmax(0, 1fr))`,
            }}
          >
            {about.stats.map((s, i) => (
              <ScrollReveal key={i} delay={(i % 3) as 0 | 1 | 2}>
                <div className="font-heading text-4xl lg:text-6xl text-accent">
                  {s.number}
                </div>
                <div className="mt-2 text-xs uppercase tracking-widish text-white/60">
                  {s.label}
                </div>
              </ScrollReveal>
            ))}
          </div>
        </section>
      )}

      {/* Approach with watermark */}
      <section className="relative py-24 lg:py-32 bg-white section-titled overflow-hidden">
        <div className="section-watermark uppercase">{brand.shortName.toLowerCase()}</div>
        <div className="container relative z-10 max-w-3xl text-center">
          <ScrollReveal>
            <Building2
              size={48}
              strokeWidth={1.25}
              className="text-accent mx-auto mb-6"
            />
            <span className="section-eyebrow">{about.approachEyebrow}</span>
            <h2 className="mt-3 text-2xl sm:text-3xl font-heading font-normal uppercase tracking-ultrawide text-brand-700">
              {about.approachTitle}
            </h2>
            <p className="mt-6 text-brand-500 leading-relaxed whitespace-pre-line">
              {about.approachText}
            </p>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
